'use client'

import { useEffect, useRef, useState } from 'react'
import { Button, Cell } from '@vinted/web-ui'
import { isNil, omitBy } from 'lodash'
import classNames from 'classnames'

import { useConversationContext } from '@marketplace-web/domain/core-conversation'
import { BundleFooterPricing } from '@marketplace-web/domain/escrow-pricing'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useBreakpoint } from '@marketplace-web/shared/breakpoints'
import { navigateToPage } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ProductItemModel } from '@marketplace-web/domain/product-item-data'
import {
  BundleShippingType,
  getBundleOrderReview,
  createBundleTransaction,
  editBundleTransaction,
  transformInstantBundleSummaryDto,
  BundleSummaryModel,
} from '@marketplace-web/domain/items-bundle-data'
import {
  TransactionDto,
  transformTransaction,
  CONVERSATION_URL,
} from '@marketplace-web/domain/conversation-messaging-data'
import { useNavigateToCheckout, navigateToSingleCheckout } from '@marketplace-web/domain/checkout'
import { CheckoutOrderTypeMap, ExtraServiceOrderType } from '@marketplace-web/domain/checkout-data'

import { buyerBuyEvent, buyerWantItemEvent, clickEvent } from '_libs/common/event-tracker/events'
import { getHomepageSessionIdFromUrl } from '_libs/utils/homepage'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import ItemThumbnails from '../ItemThumbnails'
import InstantBundleSummaryDialog from '../InstantBundleSummaryDialog'

type Props = {
  selectedItems: Array<ProductItemModel>
  transactionId: number | null
  bundleTrackingId: string | null
  bundleSummary: BundleSummaryModel | undefined
  isSellerBusiness: boolean
  homepageSessionId: string | null
  sellerId: number
  isBundleSummaryLoading: boolean
  checkoutId: string | null
}

type TransactionConfirmationProps = {
  bundleShippingType?: BundleShippingType | null
  redirectToCheckout?: boolean
  openOfferRequestModal?: boolean
}

const BundleFooter = ({
  bundleSummary,
  bundleTrackingId,
  isSellerBusiness,
  selectedItems,
  transactionId,
  homepageSessionId,
  sellerId,
  isBundleSummaryLoading,
  checkoutId,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('bundle_items')
  const breakpoints = useBreakpoint()
  const { setTransaction } = useConversationContext()

  const { navigateToCheckout: navigateAndCreateNewCheckout } = useNavigateToCheckout()

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isEditCheckoutTransactionLoading, setIsEditCheckoutTransactionLoading] = useState(false)
  const itemThumbnailsContainerRef = useRef<HTMLDivElement | null>(null)

  const selectedItemIds = selectedItems.map(({ id }) => id)

  const {
    fetch: fetchOrderReview,
    transformedData: orderReview,
    isLoading: isOrderReviewLoading,
  } = useFetch(getBundleOrderReview, response =>
    transformInstantBundleSummaryDto(response.bundle_order_review),
  )

  useEffect(() => {
    if (!selectedItems.length) return

    const itemIds = selectedItems.map(({ id }) => id)

    fetchOrderReview({ itemIds, sellerId })
  }, [fetchOrderReview, selectedItems, sellerId])

  const handleTransactionResponseSuccess = (transaction: TransactionDto) => {
    const transactionResponse = transformTransaction(transaction)

    setTransaction(transactionResponse)
  }

  const createTransaction = async ({
    bundleShippingType,
    redirectToCheckout = false,
    openOfferRequestModal = false,
  }: TransactionConfirmationProps) => {
    track(
      clickEvent({
        screen: Screen.Bundling,
        target: ClickableElement.CreateBundle,
        targetDetails: JSON.stringify(
          omitBy(
            {
              bundle_id: bundleTrackingId,
              transaction_id: transactionId,
            },
            isNil,
          ),
        ),
      }),
    )

    selectedItemIds.forEach(id =>
      track(
        buyerWantItemEvent({
          transactionId,
          itemId: id,
          button: 'create_bundle',
          globalSearchSessionId: null,
          searchSessionId: null,
          homepageSessionId,
        }),
      ),
    )

    const response = await createBundleTransaction({
      selectedItemIds,
      bundleTrackingId,
      bundleShippingType,
    })

    if ('errors' in response) return

    const {
      transaction: { id, user_msg_thread_id },
    } = response

    if (redirectToCheckout) {
      track(
        buyerBuyEvent({
          transactionId: transactionId || id,
          screen: Screen.BundleSummary,
          homepageSessionId: getHomepageSessionIdFromUrl(),
        }),
      )

      navigateAndCreateNewCheckout(id)

      return
    }

    if (openOfferRequestModal) {
      handleTransactionResponseSuccess(response.transaction)

      return
    }

    navigateToPage(CONVERSATION_URL(user_msg_thread_id))
  }

  const editTransaction = async ({
    bundleShippingType,
    redirectToCheckout = false,
    openOfferRequestModal = false,
  }: TransactionConfirmationProps) => {
    if (!transactionId) return

    const response = await editBundleTransaction({
      transactionId,
      selectedItemIds,
      bundleTrackingId,
      bundleShippingType,
    })

    if ('errors' in response) return

    const {
      transaction: { id, user_msg_thread_id },
    } = response

    if (redirectToCheckout) {
      track(
        buyerBuyEvent({
          transactionId: transactionId || id,
          screen: Screen.BundleSummary,
          homepageSessionId: getHomepageSessionIdFromUrl(),
        }),
      )

      navigateAndCreateNewCheckout(id)

      return
    }

    if (openOfferRequestModal) {
      handleTransactionResponseSuccess(response.transaction)

      return
    }

    navigateToPage(CONVERSATION_URL(user_msg_thread_id))
  }

  const editTransactionOnCheckoutFlow = async () => {
    if (!transactionId) return

    const response = await editBundleTransaction({
      transactionId,
      selectedItemIds,
      bundleTrackingId,
      bundleShippingType: orderReview?.bundleShippingType,
    })

    if ('errors' in response) return

    track(
      buyerBuyEvent({
        transactionId: transactionId || response.transaction.id,
        screen: Screen.BundleSummary,
        homepageSessionId: getHomepageSessionIdFromUrl(),
      }),
    )

    if (!checkoutId) return

    navigateToSingleCheckout(
      checkoutId,
      transactionId,
      CheckoutOrderTypeMap[ExtraServiceOrderType.Transaction],
    )
  }

  const performCheckoutFlow = () => {
    if (
      orderReview?.bundleShippingType === BundleShippingType.CustomOrNoShipping ||
      orderReview?.bundleShippingType === BundleShippingType.ShippingUnavailable
    ) {
      setIsModalVisible(prevState => !prevState)
    } else {
      setIsEditCheckoutTransactionLoading(true)
      editTransactionOnCheckoutFlow()
    }
  }

  const handleReviewBundleClick = () => {
    const isCheckoutFlow = !!checkoutId

    if (isCheckoutFlow) {
      performCheckoutFlow()

      return
    }

    setIsModalVisible(prevState => !prevState)
  }

  const renderActions = () => {
    return (
      <Button
        text={translate('actions.review')}
        disabled={!selectedItems.length || isOrderReviewLoading}
        size={Button.Size.Medium}
        styling={Button.Styling.Filled}
        inline
        onClick={handleReviewBundleClick}
        testId="bundle-footer-action-button"
        isLoading={isEditCheckoutTransactionLoading}
      />
    )
  }

  const footerClassName = classNames('u-ui-padding-vertical-small', {
    'u-ui-padding-vertical-medium': breakpoints.tabletsUp,
  })

  return (
    <>
      <div className={footerClassName}>
        <Cell
          prefix={
            <BundleFooterPricing
              isSellerBusiness={isSellerBusiness}
              selectedItems={selectedItems}
              bundleSummary={bundleSummary}
              isBundleSummaryLoading={isBundleSummaryLoading}
            />
          }
          suffix={renderActions()}
          body={
            <div className="u-tablets-up-only" ref={itemThumbnailsContainerRef}>
              <ItemThumbnails
                isDynamic
                items={selectedItems}
                containerRef={itemThumbnailsContainerRef}
              />
            </div>
          }
          styling={Cell.Styling.Tight}
        />
      </div>
      {orderReview && bundleSummary && (
        <InstantBundleSummaryDialog
          show={isModalVisible}
          onClose={handleReviewBundleClick}
          bundleSummary={bundleSummary}
          instantBundleSummary={orderReview}
          bundleTrackingId={bundleTrackingId}
          selectedItems={selectedItems}
          onConfirm={transactionId ? editTransaction : createTransaction}
          sellerId={sellerId}
        />
      )}
    </>
  )
}

export default BundleFooter
